<template>
  <div @keydown.esc.stop @keyup.esc.stop>
    <u-select-menu
      name="tag-select"
      v-model="selected"
      @update:model-value="select"
      v-model:query="inputStr"
      :searchable="getTags"
      :searchable-lazy="true"
      :searchable-placeholder="placeholder"
      clear-search-on-close
      multiple
      :disabled="disabled"
      :ui-menu="uiMenu"
      :ui="ui"
      :popper="{arrow: true, placement: 'bottom'}"
    >
      <!-- note: select multiple is necessary because otherwise a click away form the dropdown results in selecting the highlighted option -->

      <template #option="{option}">
        <template v-if="option.isRename">
          <u-avatar :icon="COMMON_ICONS.edit" size="md" :ui="{background: 'bg-charcoal-200 dark:bg-charcoal'}" />
          <span>Rename to "{{inputStr}}"</span>
        </template>

        <template v-else>
          <tag-avatar :tag="option" size="md" />
          {{option.name}}
        </template>
      </template>

      <template #option-empty>
        <div class="flex gap-2" v-if="isLoading">
          <u-icon :name="COMMON_ICONS.loading" class="animate-spin text-xl" />
          <span>Loading</span>
        </div>
        <template v-else>
          No tags found.
        </template>
     </template>

      <template #empty>
        No tags found.
      </template>

      <div ref="targetWrapperEl">
        <slot />
      </div>

    </u-select-menu>
  </div>
</template>

<script setup>
  import {TAG_SORT_OPTIONS} from '~/constants/tags.js';

  const emit = defineEmits(['merged', 'updated', 'remove']);
  const props = defineProps({
    tag: Object,
    size: {
      type: String,
      default: 'md'
    },

    renameOnly: Boolean,
    disabled: Boolean,
    ui: Object
  });

  const isLoading = ref(false);
  const tagApi = useTagApi();
  const tagsStore = useTagsStore();
  const tagActions = useTagActions();
  const selected = ref([]);
  const targetWrapperEl = ref();

  const inputStr = ref('');
  const renameOption = {
    isRename: true
  };

  const uiMenu = computed(() => {
    const emptyNameOnlyProps = props.renameOnly && !inputStr.value
      ? {
        empty: 'hidden',
        input: 'border-b-0 my-0'
      }
      : {};

    return {
      container: 'min-w-80 z-[999]',
      height: 'max-h-80',
      base: 'scrollbar-light',
      trigger: 'block h-full',
      ...emptyNameOnlyProps
    };
  });

  const placeholder = computed(() => {
    if (props.renameOnly) {
      return 'Enter a name';
    }

    return 'Enter a name or merge';
  });

  watch(inputStr, () => (isLoading.value = true));

  async function getTags(search) {
    isLoading.value = true;

    const data = await tagApi.getTags({
      search,
      order: TAG_SORT_OPTIONS[0].param
    });

    const tags = data.tags.filter(t => t.id !== props.tag.id);

    // match results exactly if search
    const filteredTags = !search
      ? tags
      : tags.filter(t => t.name.toLowerCase().includes(search.toLowerCase()));

    if (search && !tags.find(t => t.name === search)) {
      filteredTags.unshift(renameOption);
    }

    isLoading.value = false;

    return filteredTags;
  }

  async function select(options) {
    if (isLoading.value) {
      selected.value = [];
      return;
    }

    const option = options[options.length - 1];

    if (!option) {
      return;
    }

    if (option.isRename) {
      renameTag(inputStr.value);
    } else {
      selectTag(option);
    }

    if (targetWrapperEl.value) {
      targetWrapperEl.value.click();
    }

    selected.value = [];
  }

  async function renameTag(name) {

    try {
      await tagsStore.updateTag({
        id: props.tag.id,
        updates: {
          name
        }
      });

      useSuccessToast().add({
        description: 'Name updated successfully.'
      });

      emit('updated');
    } catch (e) {
      useErrorToast().add();
    }
  }

  async function selectTag(selectedTag) {
    const {merged} = await tagActions.confirmTagMerge({
      tag: props.tag,
      toTag: selectedTag
    });

    if (merged) {
      useSuccessToast().add({
        description: 'The merge was performed successfully.'
      });

      emit('merged', {tag: selectedTag});
      emit('updated');
    }
  }
</script>
